import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from "react-router-dom";

import { toggleEditMode, updateWidgetProperty, updateWidgetActions, switchStepInForm, insertFieldToForm, deleteWidgets, updateToolsSettings } from 'actions/editor';
import { useChildrenWidgetSelected, useWidgetChildrenSelector, useGroupContainerSelector, useAllowCreateLiveElement } from 'components/Editor/hooks/useEditorSession';

import _ from 'lodash';
import cx from 'classnames';
import { EditorContext } from '../../index';
import { Property } from '../../components/properties/';
import { LiveElementCreationProperty } from 'components/Editor/components/liveElements';

import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { OverlayTrigger, Popover, Button, Form, Accordion, Card, useAccordionButton, FormCheck } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons'
import { getColorsConfig } from 'services/cssHelper.service';

import { fetchCustomFormFields } from 'actions/formFields';
import { FORM_CHECK_BOX, FORM_CHECK_LIST, FORM_DROPDOWN_LIST, FORM_FIELD, OTP_INPUT, FORM_NUMBER, FORM_STEP, FORM_TEXTAREA, HOTSPOT, SECTION, SECTION_COLUMN, PAGE, CAROUSEL_CARD } from 'constants/widgetTypes';
import { getBaseUrl } from 'services/util';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';
import InfiniteScroll from 'components/InfiniteScroll';


import { createMixpanelEvent } from 'actions/mixpanel';
import { MIXPANEL_FORM_FIELDS_ADDED_TO_FORM, MIXPANEL_OTP_ENABLED } from 'constants/mixpanelEvents';
import StoryboardContext from 'components/Storyboard/context';
import { getCustomFieldType, getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';
import { getWidgetType } from 'components/Editor/helper/widget';

import styles from './form.module.scss';

const FormWidget = React.memo(function FormEditor({ className, style, widgetProps, widgetRef, children }) {
  const dispatch = useDispatch();

  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item", null));

  const [state, setState] = useState({
    formStyles: {}
  });

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  var formSteps = children.filter(item => _.get(item, 'props.widgetProps.type') === FORM_STEP)
    .map(item => _.get(item, 'props.widgetProps'));

  formSteps = _.orderBy(formSteps, ['order'], ['asc']);
  const formStep = formSteps[0];

  const rawStep = _.get(widgetProps, 'step', null);
  const validStep = getValidStep(formSteps, rawStep);
  const step = validStep ? validStep.uid : null;
  
  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const properties = _.get(widgetProps, 'properties', {});
  const deviceProperties = _.get(properties, `${breakpoint}`, {});

  const childrenSelection = useChildrenWidgetSelected(editor, id);

  Fonts.useFont(_.get(widgetProps, 'fonts') || []);

  useEffect(() => {
    var formStyles = getStyles(properties, breakpoint);
    setState(state => {
      return {
        ...state,
        formStyles: formStyles
      }
    })
  }, [deviceProperties]);

  useEffect(()=>{
    const request = {
      "workspace": workspace.id,
      "defaultFields": true,
      "query": '',
      "ignoreTypes": [], 
      "page": 0,
      "limit": 50,
      "sort": "created_date"
    }
    dispatch(fetchCustomFormFields(request))
  },[])

  var widgetStyle = {
    ...style,
    ...state.formStyles
  }

  return (
    <div id={id}
      ref={ref}
      className={cx(className, styles.form, {
        [styles.partialSelection]: childrenSelection.selected
      })}
      style={widgetStyle}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >
      {React.Children.map(children,
        child => {
          const active = _.get(child, 'props.widgetProps.uid') === step;

          return (
            React.cloneElement(child, {
              activeStep: active
            }, null)
          );
        })
      }
    </div>
  )
});

const FormWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';
  const tools = useSelector((state) => _.get(state, `${editorSession}.present.tools`));

  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const id = _.get(props, 'uid');

  var steps = useWidgetChildrenSelector(editorSession, id);
  steps = _.orderBy(steps, 'order', 'asc');
  
  const rawActiveStep = _.get(props, 'step');
  const validStep = getValidStep(steps, rawActiveStep);
  const activeStep = validStep ? validStep.uid : null;

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const handleStepChange = (step) => {
    dispatch(switchStepInForm(editorSession, id, step));
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const toggleOption = (option) => {
    const value = _.get(tools, option, false);

    var request = {
      [option]: !value,
      formLayerProps: {
        target: `#${id}`,
        props: props,
        offset: { x: 10, y: 0 },
        steps: steps
      }
    };

    dispatch(updateToolsSettings(editorSession, request));
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <FormStepDropdown steps={steps} selected={activeStep} onChange={handleStepChange} />
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='Fields' onClick={() => changeView('fields')}>
        <i className={cx("cleverstory-filters", styles.filtersIcon)} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Style' onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Action tooltip='OTP' onClick={() => changeView('otp')}>
        <i className={cx(styles.shieldIcon, "cleverstory-otp")} />
      </Property.Action>
      <Property.Action tooltip='Notification' onClick={() => changeView('email')}>
        <i className={cx(styles.mailIcon, "cleverstory-mail-notification", styles.mailIcon)} />
      </Property.Action>

      {allowLiveElement && 
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => changeView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      <Property.Action className={styles.iconButton} tooltip='Layers' onClick={() => toggleOption("formLayers")}>
        <i className={cx("cleverstory-layers")} />
      </Property.Action>

      {!editMode && view === 'fields' &&
        <AddFieldsEdiitor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'style' &&
        <FormStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {!editMode && view === 'email' &&
        <FormEmailTemplateEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'otp' &&
        <FormOtpTemplateEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
      {!editMode && view === 'brandMedia' &&
        <BrandComponentProperty type={'FORMS'} widgets={[props]} onCancel={() => closePopup()} />
      }
    </>
  );
}

const FormWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const isGroupContainer = useGroupContainerSelector(editorSession, props);
  const tools = useSelector((state) => _.get(state, `${editorSession}.present.tools`));

  const id = _.get(props, 'uid');

  var steps = useWidgetChildrenSelector(editorSession, id);
  steps = _.orderBy(steps, 'order', 'asc');
  
  const rawActiveStep = _.get(props, 'step');
  const validStep = getValidStep(steps, rawActiveStep);
  const activeStep = validStep ? validStep.uid : null;

  const handleStepChange = (step) => {
    dispatch(switchStepInForm(editorSession, id, step));
  }

  const toggleOption = (option) => {
    const value = _.get(tools, option, false);

    var request = {
      [option]: !value,
      formLayerProps: {
        target: `#${id}`,
        props: props,
        offset: { x: 10, y: 0 },
        steps: steps
      }
    };

    dispatch(updateToolsSettings(editorSession, request));
  }

  return (
    <>
      <FormStepDropdown steps={steps} selected={activeStep} onChange={handleStepChange} />

      {!isGroupContainer && allowLiveElement &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => setView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => setView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      <Property.Action className={styles.iconButton} tooltip='Layers' onClick={() => toggleOption("formLayers")}>
        <i className={cx("cleverstory-layers")} />
      </Property.Action>

      {view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => setView(null)} />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'FORMS'} widgets={[props]} onCancel={() => setView(null)} />
      }
    </>
  );
}

const FormStepDropdown = ({ steps, selected, onChange }) => {
  const [show, setShow] = useState(false);
  
  const validStep = getValidStep(steps, selected);
  const activeStep = validStep || (steps.length > 0 ? steps[0] : null);

  const handleChange = (step) => {
    onChange(step.uid);
    setShow(false);
  }

  if (!steps || steps.length === 0) {
    return null;
  }

  const popover = (
    <Popover className={styles.stepsDropdown}>
      <Popover.Body>
        {steps.map(step => {
          const active = step.uid === selected;
          return (
            <button
              key={step.uid}
              className={cx({ [styles.active]: active })}
              onClick={() => handleChange(step)}
            >{step.name || 'State - Form'}</button>
          );
        })}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      rootClose
      show={show}
      placement="bottom"
      overlay={popover}
      onHide={() => setShow(false)}
    >
      <Button className={styles.steps} onClick={() => setShow(!show)}>
        State - {activeStep ? (activeStep.name || 'Form') : 'Form'}
      </Button>
    </OverlayTrigger>
  );

}

FormWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    renderDirections: ['e', 'w', 's'],
    onReparent: (widget, parent) => {
      const type = getWidgetType(editorSession, parent)
      if (type === PAGE) return true;
      var parentElement = parent ? document.getElementById(parent) : null;
      if (parentElement && parentElement.getAttribute('data-widget-type') === HOTSPOT) {
        return false;
      }
      if (parentElement && [SECTION, SECTION_COLUMN, PAGE, CAROUSEL_CARD].includes(parentElement.getAttribute('data-widget-type'))) {
        return true;
      }

      return false
    }
  }
}

FormWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <FormWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }

      {breakpoint === 'mobile' &&
        <FormWidgetMobileAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

const AddFieldsEdiitor = ({ id, editor, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const formFieldTypes = [ FORM_FIELD, OTP_INPUT, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX ];

  const storyboardContext = React.useContext(StoryboardContext);
  const loading = useSelector((state) => _.get(state, "formFields.loading") || false);
  const pagination = useSelector((state) => _.get(state, "formFields.pagination") || {});
  const workspaceId = useSelector((state) => _.get(state, "workspaces.workspace.item.id") || null);
  const hasMoreItems = pagination.currentPage < pagination.totalPages - 1;
  const error = useSelector((state) => _.get(state, "formFields.error") || false);

  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`));
  const fields = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`);
    const steps = widgets.filter(item => item.type === FORM_STEP && item.parent === id).sort((w1, w2) => {
      const o1 = _.get(w1, 'order') || 1;
      const o2 = _.get(w2, 'order') || 1;

      return o1 - o2;
    });

    const step = _.get(steps, '0.uid');
    if (!step) {
      return [];
    }

    const fields = widgets.filter(item => _.includes(formFieldTypes, item.type) && item.parent === step).map(item => _.get(item, 'content.id') || _.get(item, 'content'));
    return fields;
  });

  const customFormFields = useSelector((state) => _.get(state, "formFields.items", null));

  const handleAddCustomField = (data) => {
    const existing = _.includes(fields, data.id);
    if (!existing) {
      dispatch(insertFieldToForm(editor, id, data));

      if(data.userDefined){
        var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type)
        mixpanelPayload = {
          ...mixpanelPayload,
          custom_form_field_type: getCustomFieldType(_.get(data, "type", ""))
        }
        dispatch(createMixpanelEvent(MIXPANEL_FORM_FIELDS_ADDED_TO_FORM, mixpanelPayload))
      }
    }
    else{
      const formFieldWidgets= widgets.filter(item => _.includes(formFieldTypes, item.type));
      const deleteWidget = formFieldWidgets.filter(item => (_.get(item,'content.id') || _.get(item, 'content')) === data.id).map(item => item.uid);
      dispatch(deleteWidgets(editor, deleteWidget));
    }
  }

  const RenderIcon = ( {fieldType} ) => {
    switch (fieldType) {
      case 'string_field':
        return <i className='cleverstory-text-layer'/>
          
      case 'list_field':
        return <i className='cleverstory-list'/>
          
      case 'number_field':
        return <i className='cleverstory-hash'/>
          
      case 'boolean_field':
        return <i className='cleverstory-checkbox'/>
          
      default:
        return <i className='cleverstory-list'/>
    }
  }

  const handleManageCustomFormFields = () => {
    let customFormFieldsURL = `${baseUrl}/integration-settings/form-fields/`;
    const win = window.open(customFormFieldsURL, "_blank");
    win.focus();
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    const request = {
      "workspace": workspaceId,
      "defaultFields": true,
      "query": '',
      "ignoreTypes": [], 
      "page": nextPage,
      "limit": 50,
      "sort": "created_date"
    }

    if(!error) {
      dispatch(fetchCustomFormFields(request));
    }
  };

  return (
    <Property.Dialog
      width={246}
      target={`#${id}`}
      title={'Add Fields'}
      onCancel={rest.onCancel}
    >
      <Property.Body className={styles.targetProperty}>
        <div className={styles.body}>
          {
            customFormFields.filter(item => !item.userDefined).map(data => {
              const checked = _.includes(fields, data.id);
              return (
                <div className={cx(styles.customFormField, { [styles.disabled]: data.name == 'Email'})}>
                  <input 
                    type="checkbox"
                    checked={checked}
                    className={cx(styles.customCheckbox, {[styles.checked]: checked})}
                    onClick={() => handleAddCustomField(data)}
                  />
                  <span className={styles.field}>
                    <RenderIcon fieldType={_.get(data, 'type', null)} />
                    <span>{data.name}</span>
                  </span>
                </div>
              )
            })
          }
        </div>
        { _.size(customFormFields) > 0 &&
          <Accordion className={styles.customFieldWrapper}>
          <div>
            <div className={styles.accordHeader}>
              <div className={styles.settings}>
                <span className={styles.label}>Custom Form Fields</span>
                <Button variant='info' onClick={()=>handleManageCustomFormFields()}>
                  <i className={cx('cleverstory-story-settings', styles.storySettingsIcon)} />
                </Button>
              </div>
              <CustomWidgetToggle eventKey="0"/>
            </div>
            <Accordion.Collapse eventKey="0">
              <InfiniteScroll
                className={styles.scrollable}
                loading={loading}
                hasMore={hasMoreItems}
                hideLoadMoreButton={true}
                loadMore={() => {
                  loadMore();
                }}
              >
                <div className={styles.body}>
                {
                  customFormFields.filter(item => item.userDefined).map(data => {
                  const checked = _.includes(fields, data.id);
                  return (
                    <div className={styles.customFormField}>
                      <input 
                        type="checkbox"
                        checked={checked}
                        className={cx(styles.customCheckbox, {[styles.checked]: checked})}
                        onChange={() => handleAddCustomField(data)}
                      />
                      <span className={styles.field}>
                      <RenderIcon fieldType={_.get(data, 'type', null)} />
                      <TruncatedTextWithTooltip content={data.name} maxWidth={160} placement='right'/>
                      </span>
                    </div>
                  )
                  })
                }
                </div>
              </InfiniteScroll>
            </Accordion.Collapse>
          </div>
          </Accordion>
        }
      </Property.Body>
    </Property.Dialog>
  )
}

function CustomWidgetToggle({ eventKey }) {
  const [open, setOpen] = useState(false);
  const decoratedOnClick = useAccordionButton(eventKey, () =>
    setOpen(!open)
  );

  return (
    <Button
      variant='info'
      className={cx({[styles.openArrow]: open})}
      onClick={decoratedOnClick}
    >
        <i className={'cleverstory-drop-down'} />
    </Button>
  );
}


const FormStyleEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const properties = _.get(widget, `properties.${breakpoint}`, {});

  const backgroundColor = _.get(properties, 'background', null);

  const border = _.get(properties, 'border', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });


  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      background: color
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      border: {
        ...border,
        color: color
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      border: {
        ...border,
        width: value
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      border: {
        ...border,
        radius: radius
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Customize Form'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Form Design" className={styles.DesignProperty}>
          <Property.Accordion title={'Border'} className={styles.borderProperty}>
            <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
            <Property name="Stroke Width" className={styles.buttonStyleProperty}>
              <Property.Slider
                min={0}
                max={10}
                step={1}
                input
                value={borderWidth}
                onChange={handleBorderWidthChange}
              />
            </Property>
            <Property name="Corner Radius" className={styles.formStyleProperty}>
              <Property.BorderRadius
                value={borderRadius}
                onChange={handleBorderRadiusChange}
              />
            </Property>
          </Property.Accordion>
          <Property.Accordion title={'Background'} className={cx(styles.borderProperty, styles.backgroundProperty)}>
            <Property.Color title={'Color'} gradient={true} value={backgroundColor} onChange={handleBackgroundColorChange} />
          </Property.Accordion>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const FormEmailTemplateEditor = ({ id, editor, widget, position, onCancel, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const properties = _.get(widget, `properties.${breakpoint}`, {});
  var emailTemplate = {
    emails: [],
    subject: "",
    body: "",
    error: null
  };

  const formEmailTemplate = _.get(properties, 'formEmailTemplate', emailTemplate) || emailTemplate;

  const [state, setState] = useState(formEmailTemplate);

  const emails = (_.get(state, 'emails') || []).map(item => {
    return {
      label: item,
      value: item
    }
  });

  const handleSaveEmailTemplate = (e) => {
    e.preventDefault();

    var request = {
      ...properties,
      formEmailTemplate: state
    }

    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
    onCancel();
  }

  const handleFieldChange = (field, e) => {
    setState((state) => {
      return {
        ...state,
        [field]: e.target.value,
        error: null,
      };
    });
  }

  const isValid = (email) => {
    let error = null;

    if (isInList(email)) {
      error = `"${email}" has already been added.`;
    }

    if (!isEmail(email)) {
      error = `"${email}" is not a valid email address.`;
    }

    if (isLimitExceeded()) {
      error = `only 5 emails are allowed`;
    }

    if (error) {
      setState(state => {
        return {
          ...state,
          error
        }
      })

      return false;
    }

    return true;
  }

  const isInList = (email) => {
    return state.emails && state.emails.includes(email);
  }

  const isLimitExceeded = () => {
    if (state.emails && state.emails.length >= 5) {
      return true;
    }

    return false;
  }

  const isEmail = (email) => {
    return /[\w\d\.-]+@[\w\d\.-]+\.[\w\d\.-]+/.test(email);
  }

  const handleEmailChange = (data) => {
    const emails = data.map(item => item.value);
    const currentValue = data[data.length - 1];

    if (data.length < state.emails.length) {
      const emails = data.map(item => item.value);
      setState((state) => {
        return {
          ...state,
          emails: emails,
          error: null
        };
      });
    }

    if (currentValue && currentValue.__isNew__) {
      if (isValid(currentValue.value)) {
        setState((state) => {
          return {
            ...state,
            emails: emails,
            error: null
          };
        });
      }
    }

    return;
  }

  return (
    <Property.Dialog
      width={604}
      target={`#${id}`}
      title={'Form submission - Email Notification Setting'}
      onCancel={onCancel}
    >
      <Property.Body className={styles.targetProperty}>
        <Form className={styles.emailFields}>
          <Form.Group className="mb-3" controlId="formEmailToAddress">
            <Form.Label>To:</Form.Label>
            <CreatableSelect
              className={styles.select}
              classNamePrefix="select"
              name="emails"
              placeholder="You can give upto 5 email-id’s"
              isMulti
              options={[]}
              value={emails}
              onChange={handleEmailChange}
            />
            <span className={styles.error}>{state.error}</span>
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>Subject</Form.Label>
            <Form.Control
              onChange={handleFieldChange.bind(this, "subject")}
              type="text"
              placeholder="Ex: We got a lead!"
              value={state.subject} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlTextarea1">
            <Form.Label>Body</Form.Label>
            <Form.Control
              onChange={handleFieldChange.bind(this, "body")}
              as="textarea"
              rows={4}
              placeholder="Ex: Someone just submitted the form on your page."
              value={state.body} />
          </Form.Group>
          <div className={styles.buttonTray}>
            <Button
              className={styles.save}
              onClick={handleSaveEmailTemplate}>
              Save
            </Button>
          </div>

        </Form>
      </Property.Body>
    </Property.Dialog>
  )
}

const FormOtpTemplateEditor = ({ id, editor, widget, position, onCancel, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const properties = _.get(widget, `properties.${breakpoint}`, {});
  const propertiesMobile = _.get(widget, `properties.mobile`, {});

  const emailOtp = false;

  const formOtpTemplate = _.get(properties, 'emailOtp') || emailOtp;

  const [state, setState] = useState(formOtpTemplate);

  const toggleOtp = (e) => {
    const isChecked = e.target.checked;
    setState(isChecked);
    var request = {
      ...properties,
      emailOtp: isChecked
    }

    var requestMobile = {
      ...propertiesMobile,
      emailOtp: isChecked
    }
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
    dispatch(updateWidgetProperty(editorSession, id, 'mobile', requestMobile));

    if(isChecked){
      const mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type)
      dispatch(createMixpanelEvent(MIXPANEL_OTP_ENABLED, mixpanelPayload))
    }
  }
  
  return (
    <Property.Dialog
      width={450}
      target={`#${id}`}
      title={'Configure OTP'}
      onCancel={onCancel}
    >
      <Property.Body className={styles.otpTargetProperty}>
        <Form className={styles.otpFields}>
          <Form.Group controlId="indexing" className={cx(styles.otpSmallGroup, styles.otpFlexFormGroup)}>
            <Form.Label className={styles.otpInlineLabel}>Enable One-Time-Passcode for email verfication</Form.Label>
            <Form.Check
              id="custom-switch"
              type="switch"
              className={styles.otpSwitchButton}
              checked={state}
            >
              <FormCheck.Input isValid className={styles.otpSwitchButton} onChange={toggleOtp} checked={state} />
            </Form.Check>
          </Form.Group>
        </Form>
      </Property.Body>
    </Property.Dialog>
  )
}

function getStyles(properties, breakpoint) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);

  const font = _.get(defaultProperties, 'font', {});

  //font-color
  const colorRgb = _.get(font, 'color', null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(font, 'fontFamily', 'inherit');
  styles = {
    ...styles,
    fontFamily: fontFamily
  }

  //font-size
  const fontSizeString = _.get(deviceProperties, 'regular.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;


  //padding
  const paddingString = _.get(deviceProperties, 'padding', null)
  var padding = '20'
  if (paddingString) {
    padding = parseInt(paddingString) + 'px';
  }

  styles['padding'] = padding;


  //font-style
  const bold = _.get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = _.get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = _.get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //background
  const backgroundRgb = _.get(defaultProperties, 'background', null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;

  //border
  const border = _.get(defaultProperties, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //alignment
  const alignment = _.get(deviceProperties, 'alignment', {});

  var justifyContent = _.get(alignment, 'justifyContent', 'left');
  styles['justifyContent'] = justifyContent;

  var flexDirection = _.get(alignment, 'flexDrection', 'column');
  styles['flexDirection'] = flexDirection;

  var alignItems = _.get(alignment, 'alignItems', 'start');
  styles['alignItems'] = alignItems;

  return styles;
}

const getValidStep = (steps, currentStepUid) => {
  if (!steps || steps.length === 0) return null;
  
  const validStepUids = steps.map(fs => fs.uid);
  const isValid = validStepUids.includes(currentStepUid);
  
  return isValid ? steps.find(s => s.uid === currentStepUid) : steps[0];
};

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var borderStyles = {}

  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    borderStyles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    borderStyles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    borderStyles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    borderStyles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  borderStyles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  borderStyles['borderWidth'] = borderWidget;

  return borderStyles;
}

FormWidget.processRequest = (widget) => {
  return widget;
}

export { FormWidget };
